import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  feedbackStatusLoadedState,
  feedbackSubmittedState,
  resultSelector,
} from "../../../utils/recoil";
import { useTranslation } from "react-i18next";
import config from "../../../config";

const FEEDBACK_QUESTIONS = [
  "result_felt_accurate",
  "career_guidance_helpful",
  "strengths_weaknesses_clear",
  "missing_important_guidance",
  "would_recommend_result",
];

const FEEDBACK_REASON_KEYS = [
  "too_generic",
  "not_personal",
  "missing_details",
  "hard_to_understand",
  "not_actionable",
];

const COMMENT_MAX_LENGTH = 240;
const DISALLOWED_COMMENT_PATTERN =
  /(https?:\/\/|www\.|<|>|`|\{|\}|\[|\]|javascript:|data:|vbscript:|on\w+\s*=)/i;
const HTML_RISK_CHARACTERS = new Set(["<", ">", "`", "{", "}", "[", "]"]);

const isKnownQuestion = (questionId) => FEEDBACK_QUESTIONS.includes(questionId);
const isKnownReason = (reason) => FEEDBACK_REASON_KEYS.includes(reason);

const cleanCommentInput = (comment) =>
  Array.from(String(comment ?? "").normalize("NFKC"))
    .filter((character) => {
      const charCode = character.charCodeAt(0);
      const isControlCharacter =
        (charCode >= 0 && charCode <= 8) ||
        charCode === 11 ||
        charCode === 12 ||
        (charCode >= 14 && charCode <= 31) ||
        charCode === 127;

      return !isControlCharacter && !HTML_RISK_CHARACTERS.has(character);
    })
    .join("")
    .slice(0, COMMENT_MAX_LENGTH);

const isSafeComment = (comment) => !DISALLOWED_COMMENT_PATTERN.test(comment);

export const getFeedbackStorageKey = (result) =>
  `resultFeedback:${result?.id ?? result?.name ?? "latest"}`;

const ResultFeedbackModal = ({ visible, onHide, onSubmitted }) => {
  const toast = useRef(null);
  const result = useRecoilValue(resultSelector);
  const { t } = useTranslation("result");
  const storageKey = useMemo(() => getFeedbackStorageKey(result), [result]);
  const [answers, setAnswers] = useState({});
  const [commentErrors, setCommentErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useRecoilState(feedbackSubmittedState);
  const [, setFeedbackStatusLoaded] = useRecoilState(
    feedbackStatusLoadedState
  );

  const allQuestionsAnswered = FEEDBACK_QUESTIONS.every(
    (questionId) => answers[questionId]?.value !== undefined
  );
  const hasInvalidComment = Object.values(commentErrors).some(Boolean);
  const canSubmit = allQuestionsAnswered && !hasInvalidComment && !isSubmitting;

  useEffect(() => {
    let isMounted = true;
    const hasLocalSubmission = Boolean(localStorage.getItem(storageKey));

    if (hasLocalSubmission) {
      setSubmitted(true);
    }

    const getFeedbackStatus = async () => {
      try {
        const response = await fetch(`${config.domain}/feedback`, {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        const rawText = await response.text();
        let responseBody = {};

        try {
          responseBody = rawText ? JSON.parse(rawText) : {};
        } catch (error) {
          responseBody = {};
        }

        if (!isMounted) return;

        const isSubmitted =
          response.ok &&
          responseBody?.statusCode === 200 &&
          responseBody?.data?.submitted === true;

        setSubmitted(isSubmitted);

        if (isSubmitted) {
          localStorage.setItem(storageKey, "true");
        } else {
          localStorage.removeItem(storageKey);
        }
        setFeedbackStatusLoaded(true);
      } catch (error) {
        if (isMounted) {
          setSubmitted(hasLocalSubmission);
          setFeedbackStatusLoaded(true);
        }
      }
    };

    getFeedbackStatus();

    return () => {
      isMounted = false;
    };
  }, [setFeedbackStatusLoaded, setSubmitted, storageKey]);

  const updateAnswer = (questionId, answer) => {
    if (!isKnownQuestion(questionId) || typeof answer !== "boolean") return;

    setSubmitError("");
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: {
        value: answer,
        reasons: answer ? [] : currentAnswers[questionId]?.reasons ?? [],
        comment: answer ? "" : currentAnswers[questionId]?.comment ?? "",
      },
    }));
  };

  const toggleReason = (questionId, reason) => {
    if (!isKnownQuestion(questionId) || !isKnownReason(reason)) return;

    setSubmitError("");
    setAnswers((currentAnswers) => {
      const currentQuestion = currentAnswers[questionId] ?? {
        value: false,
        reasons: [],
        comment: "",
      };
      const hasReason = currentQuestion.reasons.includes(reason);

      return {
        ...currentAnswers,
        [questionId]: {
          ...currentQuestion,
          reasons: hasReason
            ? currentQuestion.reasons.filter((item) => item !== reason)
            : [...currentQuestion.reasons, reason],
        },
      };
    });
  };

  const updateComment = (questionId, comment) => {
    if (!isKnownQuestion(questionId)) return;

    setSubmitError("");
    const cleanedComment = cleanCommentInput(comment);
    setCommentErrors((currentErrors) => ({
      ...currentErrors,
      [questionId]: isSafeComment(cleanedComment)
        ? ""
        : t("feedbackModal.invalidComment"),
    }));
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: {
        ...(currentAnswers[questionId] ?? {
          value: false,
          reasons: [],
        }),
        comment: cleanedComment,
      },
    }));
  };

  const getSanitizedAnswers = () => {
    const nextCommentErrors = {};

    const sanitizedAnswers = FEEDBACK_QUESTIONS.reduce((acc, questionId) => {
      const currentAnswer = answers[questionId];

      if (typeof currentAnswer?.value !== "boolean") return acc;

      const sanitizedComment = cleanCommentInput(currentAnswer.comment).trim();
      const isNegativeAnswer = currentAnswer.value === false;
      const safeReasons = isNegativeAnswer
        ? (currentAnswer.reasons ?? []).filter(isKnownReason)
        : [];

      if (sanitizedComment && !isSafeComment(sanitizedComment)) {
        nextCommentErrors[questionId] = t("feedbackModal.invalidComment");
      }

      acc[questionId] = {
        value: currentAnswer.value,
        reasons: safeReasons,
        comment: isNegativeAnswer ? sanitizedComment : "",
      };

      return acc;
    }, {});

    setCommentErrors(nextCommentErrors);

    if (Object.values(nextCommentErrors).some(Boolean)) return null;

    return sanitizedAnswers;
  };

  const handleSubmit = async () => {
    if (!allQuestionsAnswered) return;

    const sanitizedAnswers = getSanitizedAnswers();
    if (!sanitizedAnswers) return;

    const feedbackPayload = {
      // resultId: result?.id ?? null,
      // resultName: result?.name ?? data?.name ?? null,
      // mbtiType: result?.prediction?.mbti?.entity?.code ?? null,
      // hollandCode:
      //   result?.prediction?.holland?.hollandCode ??
      //   result?.prediction?.holland?.entity?.code ??
      //   null,
      answers: sanitizedAnswers,
      submittedAt: new Date().toISOString(),
    };

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const response = await fetch(`${config.domain}/feedback`, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: feedbackPayload,
        }),
      });

      const rawText = await response.text();
      let responseBody = {};

      try {
        responseBody = rawText ? JSON.parse(rawText) : {};
      } catch (error) {
        responseBody = {};
      }
      const isSuccess =
        response.ok &&
        (responseBody?.statusCode === undefined ||
          responseBody?.statusCode === 200);

      if (!isSuccess) {
        throw new Error(responseBody?.error?.msg || responseBody?.message);
      }

      localStorage.setItem(storageKey, JSON.stringify(feedbackPayload));
      setSubmitted(true);
      toast.current?.show({
        severity: "success",
        summary: t("feedbackModal.receivedTitle"),
        detail: t("feedbackModal.receivedText"),
        life: 3500,
      });
      setAnswers({});
      setCommentErrors({});
      onSubmitted?.();
      onHide();
    } catch (error) {
      const errorMessage = error?.message || t("feedbackModal.defaultError");
      setSubmitError(errorMessage);
      toast.current?.show({
        severity: "error",
        summary: t("feedbackModal.errorTitle"),
        detail: errorMessage,
        life: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        onHide={onHide}
        className="w-[42rem] md-max:w-[92vw]"
        header={
          submitted
            ? t("feedbackModal.receivedTitle")
            : t("feedbackModal.title")
        }
      >
      {submitted ? (
        <div className="flex flex-col gap-4">
          <p className="text-[#555] text-base leading-6">
            {t("feedbackModal.receivedText")}
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onHide}
              className="rounded-lg bg-[#1C75BC] px-6 py-3 font-semibold text-white"
            >
              {t("feedbackModal.close")}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <p className="text-[#555] text-base leading-6">
            {t("feedbackModal.description")}
          </p>

          <div className="flex flex-col gap-4">
            {FEEDBACK_QUESTIONS.map((questionId) => {
              const questionAnswer = answers[questionId];
              const showReasonFields = questionAnswer?.value === false;

              return (
              <div
                key={questionId}
                className="flex flex-col gap-4 border border-[#E5E7EB] rounded-lg p-4"
              >
                <div className="flex items-center justify-between gap-4 md-max:flex-col md-max:items-start">
                  <p className="text-base font-medium text-[#222]">
                    {t(`feedbackModal.questions.${questionId}`)}
                  </p>
                  <div className="flex gap-2 shrink-0 md-max:w-full">
                    {[true, false].map((answer) => {
                      const isSelected = questionAnswer?.value === answer;
                      return (
                        <button
                          key={`${questionId}-${answer}`}
                          type="button"
                          onClick={() => updateAnswer(questionId, answer)}
                          className={`min-w-[5rem] rounded-lg border px-4 py-2 font-semibold transition md-max:flex-1 ${
                            isSelected
                              ? "border-[#1C75BC] bg-[#1C75BC] text-white"
                              : "border-[#D1D5DB] bg-white text-[#333]"
                            }`}
                        >
                          {answer
                            ? t("feedbackModal.yes")
                            : t("feedbackModal.no")}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {showReasonFields && (
                  <div className="flex flex-col gap-3 rounded-lg bg-[#F8FAFC] p-3">
                    <p className="text-sm font-medium text-[#555]">
                      {t("feedbackModal.reasonPrompt")}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {FEEDBACK_REASON_KEYS.map((reason) => {
                        const isSelected =
                          questionAnswer?.reasons?.includes(reason);
                        return (
                          <button
                            key={`${questionId}-${reason}`}
                            type="button"
                            onClick={() => toggleReason(questionId, reason)}
                            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                              isSelected
                                ? "border-[#1C75BC] bg-[#EAF3FF] text-[#1C75BC]"
                                : "border-[#D1D5DB] bg-white text-[#444]"
                            }`}
                          >
                            {t(`feedbackModal.reasons.${reason}`)}
                          </button>
                        );
                      })}
                    </div>
                    <textarea
                      value={questionAnswer?.comment ?? ""}
                      onChange={(event) =>
                        updateComment(questionId, event.target.value)
                      }
                      rows={2}
                      maxLength={COMMENT_MAX_LENGTH}
                      placeholder={t("feedbackModal.commentPlaceholder")}
                      className="w-full resize-none rounded-lg border border-[#D1D5DB] p-3 text-sm outline-none focus:border-[#1C75BC]"
                    />
                    <div className="flex justify-between gap-3 text-xs text-[#666]">
                      <p className="text-red-600">
                        {commentErrors[questionId] ?? ""}
                      </p>
                      <p>
                        {(questionAnswer?.comment ?? "").length}/
                        {COMMENT_MAX_LENGTH}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {submitError && (
            <p className="text-sm font-medium text-red-600">{submitError}</p>
          )}

          <div className="flex justify-end gap-3 md-max:flex-col-reverse">
            <button
              type="button"
              onClick={onHide}
              className="rounded-lg border border-[#D1D5DB] px-5 py-3 font-semibold text-[#333]"
            >
              {t("feedbackModal.maybeLater")}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`rounded-lg px-6 py-3 font-semibold text-white ${
                canSubmit
                  ? "bg-[#1C75BC]"
                  : "cursor-not-allowed bg-[#9DB9D0]"
                }`}
            >
              {isSubmitting
                ? t("feedbackModal.submitting")
                : t("feedbackModal.submit")}
            </button>
          </div>
        </div>
      )}
      </Dialog>
    </>
  );
};

export default ResultFeedbackModal;
